import { Box, Button, ButtonGroup, Checkbox, Text, VStack } from '@chakra-ui/react';
import { useMerchantDisplayName } from '@domain/merchants/hooks/useMerchantDisplayName';
import { useMerchant } from '@domain/merchants/queries/useMerchant';
import { yupResolver } from '@hookform/resolvers';
import {
  useCheckoutDispatch,
  useCheckoutState,
  useContactInfoDispatch,
  useContactInfoState,
  useModalDispatch,
  useScreenSizeState,
} from '@olo-web/client-state';
import { ContentCard } from '@olo-web/components/atoms/ContentCard';
import { ContactInfoView } from '@olo-web/components/molecules/ContactInfoView';
import { ContactInfoForm } from '@olo-web/components/organisms/Forms/ContactInfoForm';
import { useSignup } from '@olo-web/domain/customer';
import { useLevelupCustomer } from '@olo-web/domain/customer/queries';
import { useCustomer } from '@olo-web/domain/customer/queries/useCustomer';
import { useFeatureFlags } from '@olo-web/domain/featureFlags/queries/useFeatureFlags';
import { useLevelup } from '@olo-web/domain/orders/hooks/useLevelup';
import { EAuthEntryPoints, EModalTypes } from '@olo-web/types/enums';
import { isFeatureEnabledForMerchant } from '@olo-web/utils/common/functions';
import { useIsLevelup } from '@olo-web/utils/common/hooks';
import { contactInformationValidationSchema } from '@olo-web/utils/common/validationSchema';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIsOnPremisePaymentOnly } from '../common/hooks/useIsOnPremisePaymentOnly';
import { SignInButton } from '../SignInButton';

const LegalPhoneText = ({
  onConsentChange,
  isConsentChecked,
  showConsentCheckbox,
}: {
  onConsentChange: (isChecked: boolean) => void;
  isConsentChecked: boolean;
  showConsentCheckbox: boolean;
}) => {
  const merchantName = useMerchantDisplayName();

  if (showConsentCheckbox) {
    const checkboxId = 'sms-consent-checkbox';
    const labelId = 'sms-consent-label';
    return (
      <Box
        display="flex"
        alignItems="flex-end"
        gap={3}
        textStyle="label"
        color="blackAlpha.350"
        pb={4}
      >
        <Checkbox
          id={checkboxId}
          onChange={(e) => onConsentChange(e.target.checked)}
          alignSelf="flex-start"
          isChecked={isConsentChecked}
          aria-labelledby={labelId}
        />
        <Text id={labelId}>
          I consent to receive marketing messages from {merchantName || 'this merchant'}. Reply
          UNSUBSCRIBE to opt-out.{' '}
          <a
            href="https://www.spoton.com/legal/loyalty-terms/"
            target="_blank"
            rel="noreferrer"
            aria-label="Terms and Conditions (opens in new window)"
          >
            Terms and Conditions
          </a>{' '}
          and{' '}
          <a
            href="https://www.spoton.com/legal/consumer-privacy/"
            target="_blank"
            rel="noreferrer"
            aria-label="Privacy Policy (opens in new window)"
          >
            Privacy Policy
          </a>{' '}
          apply.
        </Text>
      </Box>
    );
  }

  return (
    <Text textStyle="label" color="blackAlpha.350" pb={4}>
      By providing your phone number, you consent to receive text messages from{' '}
      {merchantName || 'this merchant'} related to your order.
    </Text>
  );
};

export const CheckoutContactInfo = (props) => {
  const isDisabled = props.isDisabled;
  const focusRingStyle = {
    outline: '3px solid #000',
    outlineOffset: '3px',
    boxShadow: 'none',
  };
  const modalDispatch = useModalDispatch();
  const { data: customerData } = useCustomer();
  const { mutateAsync: signup } = useSignup();
  const contactInfo = useContactInfoState();
  const { isEditingContactInfo, addedPayment, smsMarketingConsent } = useCheckoutState();
  const checkoutDispatch = useCheckoutDispatch();
  const { isSmDown, isXsDown, isMdDown } = useScreenSizeState();
  const contactInfoDispatch = useContactInfoDispatch();
  const { data: merchant } = useMerchant();
  const { userToken } = useLevelup();
  const { data: levelupCustomer } = useLevelupCustomer(userToken as string);
  const isOnPremiseOnlyPayment = useIsOnPremisePaymentOnly();
  const isLevelup = useIsLevelup();
  const { data: featureFlags, isError: featureFlagsError } = useFeatureFlags();

  const showConsentCheckbox =
    !featureFlagsError &&
    isFeatureEnabledForMerchant({
      merchantId: merchant?.merchantId,
      featureEnabled: featureFlags?.showSMSMarketingConsent?.on,
      allowList: featureFlags?.showSMSMarketingConsent?.allow,
      denyList: featureFlags?.showSMSMarketingConsent?.deny,
    });

  const customer = useMemo(() => {
    // levelupCustomer should only be present if and only if levelup queries
    // were ever used, to avoid any unseen scenario, an additional isLevelup
    // boolean is in place
    if (isLevelup && levelupCustomer) {
      return {
        ...levelupCustomer,
      };
    }
    if (
      contactInfo?.email &&
      contactInfo?.firstName &&
      contactInfo?.lastName &&
      contactInfo?.phoneNo
    )
      return {
        ...contactInfo,
        phoneNo: contactInfo?.phoneNo,
      };

    if (customerData)
      return {
        ...customerData,
        phoneNo: customerData?.phone,
      };
    return null;
  }, [contactInfo, customerData, levelupCustomer, isLevelup]);

  const updateUserInCheckoutState = useCallback(
    (data: ICustomer) => {
      contactInfoDispatch({
        type: 'SUBMIT',
        payload: data,
      });
      checkoutDispatch({
        type: 'SET_IS_EDITING_CONTACT_INFO',
        payload: false,
      });
    },
    [checkoutDispatch, contactInfoDispatch]
  );

  const userMustLogin = useMemo(() => {
    return !merchant?.allowGuestCheckout && !customerData?.customerId;
  }, [merchant?.allowGuestCheckout, customerData]);

  const showForm = useMemo(() => {
    return isEditingContactInfo || !customer;
  }, [customer, isEditingContactInfo]);

  const formMethods = useForm({
    resolver: yupResolver(contactInformationValidationSchema),
  });

  useEffect(() => {
    if (isEditingContactInfo && customer) {
      for (const key in customer) {
        let value = customer[key];
        if (key === 'phoneNo' && value) {
          value = value.slice(-10);
        }
      }
    }
  }, [isEditingContactInfo, customer, formMethods]);

  useEffect(() => {
    if (customerData) {
      const updatedContactInfo: ICustomer = {
        spotOnCustomerId: '',
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        email: customerData.email,
        phoneNo: customerData.phone,
      };
      updateUserInCheckoutState(updatedContactInfo);
    }
  }, [customerData, updateUserInCheckoutState]);

  const handleSignInClick = () => {
    modalDispatch({
      type: 'OPEN_MODAL',
      payload: {
        modalKey: EModalTypes.LOGIN,
        analyticsAuthEntryPoint: EAuthEntryPoints.CHECKOUT_CONTACT_INFO,
      },
    });
  };

  const handleToggleEditContactInfo = (): void => {
    checkoutDispatch({
      type: 'TOGGLE_IS_EDITING_CONTACT_INFO',
    });
  };

  const handleSuccess = async (data: ICustomer) => {
    try {
      await signup({
        ...data,
        phone: data.phoneNo,
      });
      updateUserInCheckoutState(data);
    } catch (e) {
      updateUserInCheckoutState(data);
    } finally {
      if (isEditingContactInfo && isXsDown && addedPayment) {
        document.getElementById('place-order-container')?.scrollIntoView();
      }
    }
  };

  const handleEditClick = useCallback(() => {
    checkoutDispatch({
      type: 'SET_IS_EDITING_CONTACT_INFO',
      payload: true,
    });
  }, [checkoutDispatch]);

  const handleSmsConsentChange = useCallback(
    (isChecked: boolean) => {
      checkoutDispatch({
        type: 'SET_SMS_MARKETING_CONSENT',
        payload: isChecked,
      });
    },
    [checkoutDispatch]
  );

  const isFullWidth = !isEditingContactInfo && isSmDown;

  return (
    <ContentCard
      title="Contact information"
      isDisabled={isDisabled}
      rightTitleAdornment={
        !userMustLogin &&
        customer &&
        !isEditingContactInfo &&
        !isDisabled && (
          <Button
            px={0}
            variant="tertiary"
            colorScheme="primary"
            color="primary.350"
            textDecor="underline"
            fontWeight="normal"
            size="md"
            height="30px"
            onClick={handleEditClick}
            data-testid="edit-contact-information-button"
            position={isMdDown ? 'absolute' : 'relative'}
            right={isMdDown ? '10px' : 0}
            aria-label="Edit contact information"
          >
            Edit
          </Button>
        )
      }
    >
      <VStack
        alignItems="flex-start"
        justifyContent="flex-start"
        w="100%"
        data-testid="contact-info-form"
      >
        <AnimatePresence initial={false} exitBeforeEnter>
          {userMustLogin ? (
            <Box key="must-login">
              <VStack align="flex-start" spacing={4}>
                <Text textStyle="p">
                  Sign in or create an account to add your contact information.
                </Text>
                <Button
                  w={isFullWidth && '100%'}
                  alignSelf="flex-end"
                  variant="solid"
                  size="lg"
                  colorScheme="primary"
                  onClick={handleSignInClick}
                >
                  Sign in or create an account
                </Button>
              </VStack>
            </Box>
          ) : showForm ? (
            <VStack key="form" alignItems="flex-start" justifyContent="flex-start">
              <SignInButton>Sign in to use your saved information</SignInButton>
              <FormProvider {...formMethods}>
                <ContactInfoForm
                  firstName={contactInfo?.firstName || customerData?.firstName}
                  lastName={contactInfo?.lastName || customerData?.lastName}
                  email={contactInfo?.email || customerData?.email}
                  phoneNo={contactInfo?.phoneNo || customerData?.phone || customerData?.phoneNo}
                />
              </FormProvider>
              <LegalPhoneText
                onConsentChange={handleSmsConsentChange}
                isConsentChecked={smsMarketingConsent}
                showConsentCheckbox={showConsentCheckbox}
              />
              <ButtonGroup size="md" alignSelf={isFullWidth ? 'stretch' : 'flex-end'} spacing={4}>
                {isEditingContactInfo && (
                  <Button
                    variant="outline"
                    colorScheme="primary"
                    fontSize="lg"
                    onClick={handleToggleEditContactInfo}
                    data-testid="cancel-update-info-button"
                    _focus={{ boxShadow: 'none' }}
                    _focusVisible={focusRingStyle}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="solid"
                  colorScheme="primary"
                  onClick={formMethods.handleSubmit(handleSuccess)}
                  fontSize="lg"
                  type="submit"
                  w={isFullWidth && '100%'}
                  alignSelf="flex-end"
                  data-testid={
                    isEditingContactInfo ? 'update-info-button' : 'continue-to-payment-button'
                  }
                  _focus={{ boxShadow: 'none' }}
                  _focusVisible={focusRingStyle}
                >
                  {isEditingContactInfo
                    ? 'Update information'
                    : isOnPremiseOnlyPayment
                    ? 'Continue to Review'
                    : 'Continue to payment'}
                </Button>
              </ButtonGroup>
            </VStack>
          ) : (
            <Box key="info">
              <VStack align="start" justify="start">
                <ContactInfoView customer={customer as ICustomer} hideTitle={true} />
                <LegalPhoneText
                  onConsentChange={handleSmsConsentChange}
                  isConsentChecked={smsMarketingConsent}
                  showConsentCheckbox={showConsentCheckbox}
                />
              </VStack>
            </Box>
          )}
        </AnimatePresence>
      </VStack>
    </ContentCard>
  );
};
